// PDP media carousel — vertical peek-scroll on desktop, horizontal
// one-at-a-time on mobile/portrait-tablet — using IntersectionObserver and
// native scroll-snap with scrollIntoView. Axis is re-evaluated on
// resize/orientation change so rotating a tablet swaps behavior live.

import { WheelIntent, wheelDeltaPx } from '@theme/wheel-intent';

const MOBILE_QUERY = '(max-width: 767px), (max-width: 1024px) and (orientation: portrait)';

// Fraction of a slide that must be visible for it to count as the active one.
const ACTIVE_SLIDE_RATIO = 0.6;

// How many dots the indicator shows at once. Past this the strip windows and
// slides rather than growing — keep in sync with the `dots_visible` cap in
// sections/shabibi-product-main.liquid, which sizes the window to match.
const DOTS_VISIBLE = 5;

// Duration for a single programmatic step (wheel, click, drag-release).
// Matches the 0.3s transition already on .shabibi-pdp-media-slide's own
// opacity/scale/blur fade (section-shabibi-product-main.css) so the position
// change and the peek fade that accompanies it read as one motion, not two
// animations running on different clocks.
const STEP_DURATION_MS = 300;

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

// Owned rAF tween in place of `scrollTo({ behavior: 'smooth' })`: the
// browser's own smooth-scroll duration/easing is inconsistent across
// engines, and — more importantly — calling it again on a track that's
// already mid-scroll leaves retargeting behavior up to each browser, which
// tends to show as a jerk. `token` lets a newer call cancel an older one by
// simply out-aging it; the stale loop notices next frame and resolves
// without touching scroll position again.
function animateScrollTo(el, axis, target, duration, getToken, token) {
  const prop = axis === 'x' ? 'scrollLeft' : 'scrollTop';
  const start = el[prop];
  const distance = target - start;
  if (distance === 0) return Promise.resolve();

  const startTime = performance.now();
  let settled = false;

  return new Promise((resolve) => {
    const finish = () => {
      if (settled) return;
      settled = true;
      // Only this animation's own call is allowed to write the final
      // position — a superseded (cancelled) one just resolves quietly.
      if (getToken() === token) el[prop] = target;
      resolve();
    };

    // Safety net: rAF pauses on a backgrounded/hidden tab, so a gesture that
    // starts a step right before the tab loses visibility would otherwise
    // never resolve, stranding stepAnimDone permanently false and every later
    // wheel step queued behind it. A generous multiple of the intended
    // duration jumps straight to the target instead of leaving it stuck.
    window.setTimeout(finish, duration * 4);

    function step(now) {
      if (settled) return;
      if (getToken() !== token) {
        settled = true;
        resolve();
        return;
      }
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      el[prop] = start + distance * easeOutCubic(progress);
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        finish();
      }
    }
    requestAnimationFrame(step);
  });
}

class ShabibiPdpMedia extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.slides = [];
    this.dots = [];
    this.dotStrip = null;
    this.activeIndex = 0;
    this.observer = null;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.startScrollLeft = 0;
    this.startScrollTop = 0;
    this.justDragged = false;
    this.stepAnimDone = true;
    this.scrollAnimToken = 0;
    this.mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.handleAxisChange = this.handleAxisChange.bind(this);
  }

  get isMobile() {
    return this.mobileQuery.matches;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-carousel-slide]'));
    if (!this.slides.length) return;

    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.dotStrip = this.querySelector('[data-carousel-dot-strip]');

    // A single image has no position to indicate and nothing to snap to.
    // Snap is dropped via the class rather than left on, because a lone
    // mandatory snap target can trap a stray scroll offset.
    if (this.slides.length < 2) {
      this.classList.add('is-single');
    }

    this.setupPadding();
    this.initObserver();
    this.initSlideClicks();
    this.initDrag();
    this.initWheel();

    this.mobileQuery.addEventListener('change', this.handleAxisChange);

    // Resize observer for dynamic centering padding adjustment
    this.resizeObserver = new ResizeObserver(() => {
      this.setupPadding();
    });
    this.resizeObserver.observe(this.track);

    // Initial positioning on first slide
    const resetToFirstSlide = () => {
      this.goTo(0, false);
    };

    requestAnimationFrame(resetToFirstSlide);
    window.addEventListener('pageshow', resetToFirstSlide);
    this.resetToFirstSlide = resetToFirstSlide;
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.resetToFirstSlide) window.removeEventListener('pageshow', this.resetToFirstSlide);
    this.mobileQuery.removeEventListener('change', this.handleAxisChange);
  }

  // Rotating a tablet across the portrait/landscape boundary swaps the
  // carousel's scroll axis live, so re-run layout/positioning for the
  // now-current axis instead of leaving stale inline padding/scroll state.
  handleAxisChange() {
    this.setupPadding();
    this.goTo(this.activeIndex, false);
  }

  // Clears any inline centring padding so the stylesheet governs both axes.
  //
  // This used to compute the inset from measurements on both axes, and on
  // both it was degenerate — the measured slide fills the track's *content*
  // box, so the value it derives is the padding already applied. Feed that
  // back and you get p = p: every value is a fixed point, not just the right
  // one. Mobile got stuck on whatever a bad mid-render read wrote, exposing a
  // sliver of the neighbour; desktop was worse, since with a 62% basis the
  // algebra solves to p = half the track and a zero-height slide, so each
  // ResizeObserver pass walked the padding up and pushed the neighbouring
  // image out of frame.
  //
  // Both axes are now declared in section-shabibi-product-main.css against
  // the hero height, which nothing here feeds back into. This method only
  // undoes any inline value left over from a previous build.
  setupPadding() {
    if (!this.track) return;

    this.track.style.paddingInline = '';
    this.track.style.paddingBlock = '';
  }

  initObserver() {
    // 0.6, not 0.5: at exactly half, two neighbouring slides can both report
    // isIntersecting on the same frame mid-scroll and whichever entry is
    // processed last wins, which flickers the active indicator. Above half,
    // only one slide can ever qualify.
    const options = {
      root: this.track,
      threshold: ACTIVE_SLIDE_RATIO
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // >= rather than >: the observer fires *on* the crossing, so a
        // report landing exactly on the threshold has to be accepted or the
        // slide is skipped until the ratio crosses again.
        if (entry.isIntersecting && entry.intersectionRatio >= ACTIVE_SLIDE_RATIO) {
          const index = this.slides.indexOf(entry.target);
          if (index !== -1) {
            this.setActiveIndex(index);
          }
        }
      });
    }, options);

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  setActiveIndex(index) {
    if (this.activeIndex === index && this.slides[index]?.classList.contains('is-active')) return;
    this.activeIndex = index;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });

    this.updateDotWindow(index);
  }

  /**
   * Slides the dot strip so the active dot stays inside the five-dot window,
   * centred where there's room and pinned at either end otherwise.
   */
  updateDotWindow(index) {
    if (!this.dotStrip || this.dots.length <= DOTS_VISIBLE) return;

    // Pitch is measured rather than hardcoded so it tracks the CSS, and
    // it's re-read each time rather than cached so an early zero (before
    // layout) corrects itself on the next slide change instead of sticking.
    // Safe to measure here, unlike the track's centering inset: offsetLeft
    // is layout-based and unaffected by the transform this sets, so there's
    // no feedback loop.
    const pitch = this.dots[1].offsetLeft - this.dots[0].offsetLeft;
    if (pitch <= 0) return;

    const maxOffset = this.dots.length - DOTS_VISIBLE;
    const centred = index - Math.floor(DOTS_VISIBLE / 2);
    const offset = Math.min(Math.max(centred, 0), maxOffset);

    this.dotStrip.style.transform = `translateX(${-offset * pitch}px)`;
  }

  goTo(index, smooth = true) {
    if (index < 0 || index >= this.slides.length) return Promise.resolve();
    const targetSlide = this.slides[index];
    if (!targetSlide || !this.track) return Promise.resolve();

    this.setActiveIndex(index);

    const axis = this.isMobile ? 'x' : 'y';
    const target = this.isMobile
      ? Math.max(0, targetSlide.offsetLeft - (this.track.clientWidth - targetSlide.clientWidth) / 2)
      : Math.max(0, targetSlide.offsetTop - (this.track.clientHeight - targetSlide.clientHeight) / 2);

    if (!smooth) {
      this.track[axis === 'x' ? 'scrollLeft' : 'scrollTop'] = target;
      return Promise.resolve();
    }

    const token = ++this.scrollAnimToken;
    return animateScrollTo(this.track, axis, target, STEP_DURATION_MS, () => this.scrollAnimToken, token);
  }

  initSlideClicks() {
    this.track.addEventListener('click', (e) => {
      if (this.justDragged) {
        e.preventDefault();
        return;
      }

      const slide = e.target.closest('[data-carousel-slide]');
      if (!slide) return;

      const index = this.slides.indexOf(slide);
      if (index !== -1 && index !== this.activeIndex) {
        e.preventDefault();
        this.goTo(index, true);
      }
    });
  }

  initDrag() {
    const track = this.track;
    const DRAG_THRESHOLD = 5;

    const onPointerDown = (e) => {
      if (e.pointerType === 'touch') return;
      this.isDragging = true;
      this.hasCaptured = false;
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startScrollLeft = track.scrollLeft;
      this.startScrollTop = track.scrollTop;
    };

    const onPointerMove = (e) => {
      if (!this.isDragging) return;
      const delta = this.isMobile ? e.clientX - this.startX : e.clientY - this.startY;

      if (!this.hasCaptured) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        this.hasCaptured = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(this.pointerId);
      }

      if (this.isMobile) {
        track.scrollLeft = this.startScrollLeft - delta;
      } else {
        track.scrollTop = this.startScrollTop - delta;
      }
    };

    const endDrag = () => {
      if (!this.isDragging) return;
      this.isDragging = false;

      if (this.hasCaptured) {
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(this.pointerId)) {
          track.releasePointerCapture(this.pointerId);
        }

        this.snapToNearest();

        this.justDragged = true;
        requestAnimationFrame(() => {
          this.justDragged = false;
        });
      }
    };

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
  }

  snapToNearest() {
    let closestIndex = 0;
    let minDistance = Infinity;

    if (this.isMobile) {
      const trackCenter = this.track.scrollLeft + this.track.clientWidth / 2;
      this.slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(trackCenter - slideCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });
    } else {
      const trackCenter = this.track.scrollTop + this.track.clientHeight / 2;
      this.slides.forEach((slide, index) => {
        const slideCenter = slide.offsetTop + slide.clientHeight / 2;
        const distance = Math.abs(trackCenter - slideCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });
    }

    this.goTo(closestIndex, true);
  }

  initWheel() {
    const hero = this.closest('.shabibi-pdp-hero') || this;
    const intent = new WheelIntent();
    // A swipe that lands while the previous step is still animating waits
    // here and plays as soon as that step finishes, rather than being
    // dropped (felt as a dead pause) or retargeting mid-tween (felt as a
    // jerk). One slot, latest direction wins.
    let queuedDir = 0;

    const step = (dir) => {
      if (!this.stepAnimDone) {
        queuedDir = dir;
        return;
      }
      const nextIndex = this.activeIndex + dir;
      if (nextIndex < 0 || nextIndex >= this.slides.length) return;

      this.stepAnimDone = false;
      this.goTo(nextIndex, true).then(() => {
        this.stepAnimDone = true;
        if (queuedDir) {
          const next = queuedDir;
          queuedDir = 0;
          step(next);
        }
      });
    };

    hero.addEventListener(
      'wheel',
      (e) => {
        // Wheel-driven stepping is a desktop/trackpad affordance for the
        // vertical carousel only — mobile/portrait-tablet relies on touch
        // swipe against the horizontal carousel instead.
        if (this.isMobile) return;

        // If scrolling inside the product info overlay card, let it scroll natively
        if (e.target.closest('.shabibi-pdp-col--info')) return;

        // Must run on every event, before any filtering. Chrome and Safari
        // only make the first wheel event of a trackpad scroll cancelable;
        // leave that one uncancelled and the rest of the swipe can't be
        // cancelled either, so the track scrolls natively and scroll-snap
        // carries it past several slides on top of the steps taken here.
        // Trackpad swipes open with a 1–4px tick, so an earlier
        // `if (|delta| < 5) return` ahead of this was the skipping bug.
        e.preventDefault();

        const delta = wheelDeltaPx(e.deltaY, e.deltaMode, this.track.clientHeight);
        if (delta === 0) return;

        const dir = intent.feed(delta, e.timeStamp);
        if (dir) step(dir);
      },
      { passive: false }
    );
  }
}

if (!customElements.get('shabibi-pdp-media')) {
  customElements.define('shabibi-pdp-media', ShabibiPdpMedia);
}
